import { seoCountryByCode, SEO_INDEX_MIN_ITEMS } from '@/lib/seo/countries'

/**
 * THE TELEGRAM GROUP DIRECTORY, configured in exactly one place.
 *
 * Pure and browser-safe. This is a SECOND SEO surface, deliberately separate
 * from `/buy/<country>`:
 *
 *   /buy/<country>               Bazio's own Store inventory. Exists only for a
 *                                country in `SEO_COUNTRIES`, and only indexes on
 *                                real distinct Store items (`@/lib/seo/countries`).
 *   /telegram-groups/<country>   An EDITORIAL directory of public Telegram
 *                                communities the team has checked. It may exist
 *                                long before Bazio holds a single listing there.
 *
 * Nothing here ever makes a `/buy/` page exist. A directory country with no
 * Store inventory links to no `/buy/` page, and `buyPageStatus` is the one rule
 * the admin summary asks before suggesting one.
 *
 * Every directory entry is a curated database row. This file holds only the
 * page furniture: which countries have a directory, their permanent URL
 * segments, their own editorial copy, the cities an editor may file a group
 * under, and the freshness and indexing rules. It never names a group.
 */

/* ------------------------------------------------------------------ *
 *  Categories
 * ------------------------------------------------------------------ */

export type DirectoryCategory = {
    /** The permanent URL segment. */
    slug: string
    /** As it reads in a heading: "Buy & Sell". */
    name: string
    /** As it reads mid-sentence: "buying and selling". */
    phrase: string
}

/**
 * Only categories listed here have a page. A category is added when the team
 * has curated enough groups for it, never in anticipation: an empty
 * `/telegram-groups/uae/cars` is exactly the thin page this layer must not make.
 */
export const DIRECTORY_CATEGORIES: ReadonlyArray<DirectoryCategory> = [
    { slug: 'buy-sell', name: 'Buy & Sell', phrase: 'buying and selling' }
]

export const directoryCategoryBySlug = (slug: string | null | undefined): DirectoryCategory | null =>
    DIRECTORY_CATEGORIES.find((category) => category.slug === (slug ?? '').toLowerCase()) ?? null

/* ------------------------------------------------------------------ *
 *  Languages
 * ------------------------------------------------------------------ */

/**
 * The languages an editor may tag a group with, as lowercase BCP-47 codes. A
 * tag records what the community actually posts in; it is never inferred from
 * the country.
 */
export const DIRECTORY_LANGUAGES: ReadonlyArray<{ code: string, label: string }> = [
    { code: 'en', label: 'English' },
    { code: 'ar', label: 'Arabic' },
    { code: 'ru', label: 'Russian' },
    { code: 'hi', label: 'Hindi' },
    { code: 'ur', label: 'Urdu' },
    { code: 'fa', label: 'Persian' },
    { code: 'tl', label: 'Filipino' },
    { code: 'ml', label: 'Malayalam' },
    { code: 'id', label: 'Bahasa Indonesia' },
    { code: 'jv', label: 'Javanese' },
    { code: 'tr', label: 'Turkish' },
    { code: 'ms', label: 'Malay' },
    { code: 'zh', label: 'Chinese' },
    { code: 'ta', label: 'Tamil' },
    { code: 'fr', label: 'French' },
    { code: 'de', label: 'German' }
]

export const directoryLanguageLabel = (code: string): string =>
    DIRECTORY_LANGUAGES.find((language) => language.code === code)?.label ?? code.toUpperCase()

export const isDirectoryLanguage = (code: string): boolean =>
    DIRECTORY_LANGUAGES.some((language) => language.code === code)

/* ------------------------------------------------------------------ *
 *  Countries
 * ------------------------------------------------------------------ */

export type DirectoryFaq = { q: string, a: string }

export type DirectoryCountry = {
    /** ISO 3166-1 alpha-2, as stored in `telegram_directory_entries.country_code`. */
    code: string
    /** The permanent URL segment under `/telegram-groups/`. Hand-written, never derived. */
    slug: string
    /** Full English name, as the place list holds it. */
    name: string
    /** The form used in titles and headings: "UAE", "Saudi Arabia". */
    label: string
    /** The form used mid-sentence: "the UAE". */
    inPhrase: string
    /** One short paragraph for the country hub. */
    hubIntro: string
    /** The buy and sell page's own copy. Written per country, never templated. */
    buySell: {
        description: string
        intro: string
        context: string
        faq: ReadonlyArray<DirectoryFaq>
    }
    /**
     * The places an editor may file a group under, in the order city sections
     * appear on the page. A section is drawn only when a listed group carries
     * that city, so nothing here ever renders on its own.
     */
    cities: ReadonlyArray<string>
    /** Languages offered first in the editor for this country. A hint, never a tag. */
    languageHints: ReadonlyArray<string>
    /** Neighbouring directory countries suggested at the foot of the page. */
    related: ReadonlyArray<string>
}

const OWNER_FAQ = (label: string): DirectoryFaq => ({
    q: `Can my ${label} Telegram community join Bazio?`,
    a: 'Yes. Apply from the For communities page. Once approved, you connect your own Telegram bot to your group, and new posts become searchable listings on your own marketplace while the group keeps working exactly as before. Listings keep the language the seller wrote them in.'
})

export const DIRECTORY_COUNTRIES: ReadonlyArray<DirectoryCountry> = [
    {
        code: 'AE',
        slug: 'uae',
        name: 'United Arab Emirates',
        label: 'UAE',
        inPhrase: 'the UAE',
        hubIntro: 'Public Telegram communities across the Emirates, sorted by what they are for. Every group listed has a public link the Bazio team has checked.',
        buySell: {
            description: 'Discover verified public Telegram buy and sell groups in the UAE, including Dubai classifieds, second-hand marketplaces and UAE-wide communities. Links are checked by Bazio.',
            intro: 'Explore public Telegram groups where people buy, sell and exchange items across the UAE. The current directory includes Dubai-focused and UAE-wide communities, with public links checked by the Bazio team.',
            context: 'The groups currently listed are mainly Russian-language marketplace communities. More languages and locations will be added as additional public UAE groups are verified.',
            faq: [
                {
                    q: 'What are Telegram buy and sell groups?',
                    a: 'They are Telegram groups or channels where members post what they want to sell or are looking for. You browse the posts, message the seller on Telegram and arrange the deal between you. Nothing is bought or paid for through Bazio.'
                },
                {
                    q: 'How do I join a UAE Telegram marketplace?',
                    a: 'Use the Open on Telegram button on any card. A public group opens straight in the Telegram app, where you can join in one tap. Read the pinned rules before posting, because many groups limit how often you can advertise.'
                },
                {
                    q: 'Can I sell items through these groups?',
                    a: 'Usually, within each group’s own rules. Some only accept certain categories, or ask for a price and an emirate in every post. Each group’s administrators decide what is allowed, not Bazio.'
                },
                OWNER_FAQ('UAE')
            ]
        },
        cities: ['Dubai', 'Abu Dhabi', 'Sharjah', 'Ajman', 'Ras Al Khaimah', 'Fujairah', 'Umm Al Quwain', 'Al Ain'],
        languageHints: ['en', 'ar', 'ru', 'hi', 'ur'],
        related: ['SA', 'TR']
    },
    {
        code: 'SA',
        slug: 'saudi-arabia',
        name: 'Saudi Arabia',
        label: 'Saudi Arabia',
        inPhrase: 'Saudi Arabia',
        hubIntro: 'Public Telegram communities across the Kingdom, sorted by what they are for. Every group listed has a public link the Bazio team has checked.',
        buySell: {
            description: 'Find public Telegram buy and sell groups in Saudi Arabia. Discover communities for Riyadh, Jeddah and other cities for products, services and local marketplace posts.',
            intro: 'Public Telegram groups for buying and selling in Saudi Arabia, from electronics and furniture to cars, household goods and local services. Every group below is open to join, and its link was checked by the Bazio team.',
            context: 'Most Saudi groups post in Arabic, and some in English as well. Many serve a single city, so the location on each card tells you whether a group is useful for a meet-up near you. Prices are normally given in riyals; confirm the amount with the seller before you agree.',
            faq: [
                {
                    q: 'How do I find Telegram buy and sell groups in Saudi Arabia?',
                    a: 'Start with the groups on this page: each one is public, open to join and has had its link checked. Use the Open on Telegram button to join from the Telegram app, and read each group’s pinned rules first.'
                },
                {
                    q: 'Are there Telegram marketplace groups for Riyadh and Jeddah?',
                    a: 'When we have checked a public group for a city, it appears on this page with that city on its card. If a city has no group here yet, we have not verified one for it, and you can suggest a group below.'
                },
                {
                    q: 'Is it safe to buy from Telegram groups?',
                    a: 'Treat every post as a private sale. Meet in a public place, inspect the item before paying, and never send a deposit to someone you cannot verify. The group’s administrators moderate their own group.'
                },
                OWNER_FAQ('Saudi')
            ]
        },
        cities: ['Riyadh', 'Jeddah', 'Dammam', 'Khobar', 'Dhahran', 'Mecca', 'Medina', 'Taif', 'Abha', 'Tabuk'],
        languageHints: ['ar', 'en'],
        related: ['AE', 'TR']
    },
    {
        code: 'ID',
        slug: 'indonesia',
        name: 'Indonesia',
        label: 'Indonesia',
        inPhrase: 'Indonesia',
        hubIntro: 'Public Telegram communities across Indonesia, sorted by what they are for. Every group listed has a public link the Bazio team has checked.',
        buySell: {
            description: 'Discover public Telegram groups for jual beli in Indonesia. Find buy and sell communities for Jakarta, Surabaya, Bandung and other cities, with regularly checked links.',
            intro: 'Public Telegram communities for jual beli, second-hand goods and local marketplace posts across Indonesia. Every group below is open to join, and its link was checked by the Bazio team.',
            context: 'Most Indonesian groups post in Bahasa Indonesia, and many carry their city in the group name, because buyers often meet the seller in person or arrange COD locally. Look for a group in your own city first, then the country-wide ones.',
            faq: [
                {
                    q: 'What does jual beli mean?',
                    a: 'Jual beli is Indonesian for buying and selling. A grup jual beli on Telegram is a group where members post things for sale, or things they want to buy.'
                },
                {
                    q: 'How do I join an Indonesian Telegram jual beli group?',
                    a: 'Use the Open on Telegram button on any card. A public group opens in the Telegram app, where you can join in one tap. Most groups pin their rules, including what you may post and how often.'
                },
                {
                    q: 'Can I sell items through these groups?',
                    a: 'Usually, within each group’s own rules. Some groups only accept certain categories or ask for a price and a city in every post. The group’s administrators decide what is allowed, not Bazio.'
                },
                OWNER_FAQ('Indonesian')
            ]
        },
        cities: ['Jakarta', 'Surabaya', 'Bandung', 'Medan', 'Bekasi', 'Tangerang', 'Depok', 'Semarang', 'Yogyakarta', 'Denpasar', 'Makassar', 'Palembang', 'Batam'],
        languageHints: ['id', 'en', 'jv'],
        related: ['MY']
    },
    {
        code: 'TR',
        slug: 'turkey',
        name: 'Turkey',
        label: 'Turkey',
        inPhrase: 'Turkey',
        hubIntro: 'Public Telegram communities across Turkey, sorted by what they are for. Every group listed has a public link the Bazio team has checked.',
        buySell: {
            description: 'Find public Telegram buy and sell groups in Turkey. Discover communities for second-hand goods, electronics and local services in Istanbul, Ankara, Izmir and other cities.',
            intro: 'Public Telegram groups for buying and selling in Turkey, including second-hand goods, electronics and local services. Every group below is open to join, and its link was checked by the Bazio team.',
            context: 'Turkish groups mostly post in Turkish, and many are tied to one city, such as Istanbul, Ankara or Izmir. Second-hand trade is often listed under ikinci el. Prices are usually in Turkish lira, so confirm the amount and the currency before you agree.',
            faq: [
                {
                    q: 'What is an ikinci el group?',
                    a: 'İkinci el means second-hand in Turkish. Many Turkish Telegram groups focus on second-hand items, alongside new goods and services.'
                },
                {
                    q: 'How do I join a Turkish Telegram marketplace group?',
                    a: 'Use the Open on Telegram button on any card. A public group opens in the Telegram app, where you can join in one tap. Read the pinned rules before you post.'
                },
                {
                    q: 'Is it safe to buy from Telegram groups?',
                    a: 'Treat every post as a private sale. Meet in a public place, check the item before paying, and never send money in advance to someone you cannot verify. Each group’s administrators moderate their own group.'
                },
                OWNER_FAQ('Turkish')
            ]
        },
        cities: ['Istanbul', 'Ankara', 'Izmir', 'Bursa', 'Antalya', 'Adana', 'Konya', 'Gaziantep', 'Kocaeli', 'Mersin'],
        languageHints: ['tr', 'en'],
        related: ['AE', 'SA']
    },
    {
        code: 'MY',
        slug: 'malaysia',
        name: 'Malaysia',
        label: 'Malaysia',
        inPhrase: 'Malaysia',
        hubIntro: 'Public Telegram communities across Malaysia, sorted by what they are for. Every group listed has a public link the Bazio team has checked.',
        buySell: {
            description: 'Find public Telegram buy and sell groups in Malaysia. Discover jual beli communities for Kuala Lumpur, Selangor, Johor Bahru, Penang and other areas.',
            intro: 'Public Telegram groups for buying and selling in Malaysia, from Kuala Lumpur and Selangor to Johor Bahru and Penang. Every group below is open to join, and its link was checked by the Bazio team.',
            context: 'Malaysian groups post in English, Malay or Chinese, and sometimes all three in one group. Many are organised by state or city, and sellers often offer a meet-up or postage within Malaysia, so check the location on each card first.',
            faq: [
                {
                    q: 'What are Telegram jual beli groups in Malaysia?',
                    a: 'Jual beli is Malay for buying and selling. These are Telegram groups where members post items for sale or things they are looking for, and arrange the deal with each other directly.'
                },
                {
                    q: 'How do I join a Malaysian Telegram marketplace group?',
                    a: 'Use the Open on Telegram button on any card. A public group opens in the Telegram app, where you can join in one tap. Most groups pin their rules, so read those before posting.'
                },
                {
                    q: 'Can I sell items through these groups?',
                    a: 'Usually, within each group’s own rules. Some only accept certain items or ask for a price and a location in every post. Each group’s administrators decide what is allowed, not Bazio.'
                },
                OWNER_FAQ('Malaysian')
            ]
        },
        cities: ['Kuala Lumpur', 'Selangor', 'Petaling Jaya', 'Shah Alam', 'Johor Bahru', 'Penang', 'Ipoh', 'Melaka', 'Kota Kinabalu', 'Kuching'],
        languageHints: ['en', 'ms', 'zh', 'ta'],
        related: ['ID']
    }
]

export const directoryCountryBySlug = (slug: string | null | undefined): DirectoryCountry | null =>
    DIRECTORY_COUNTRIES.find((country) => country.slug === (slug ?? '').toLowerCase()) ?? null

export const directoryCountryByCode = (code: string | null | undefined): DirectoryCountry | null =>
    DIRECTORY_COUNTRIES.find((country) => country.code === (code ?? '').toUpperCase()) ?? null

/* ------------------------------------------------------------------ *
 *  Addresses
 * ------------------------------------------------------------------ */

export const DIRECTORY_INDEX_PATH = '/telegram-groups'

export const directoryCountryPath = (country: Pick<DirectoryCountry, 'slug'>): string =>
    `${DIRECTORY_INDEX_PATH}/${country.slug}`

export const directoryCategoryPath = (
    country: Pick<DirectoryCountry, 'slug'>,
    category: Pick<DirectoryCategory, 'slug'>
): string => `${directoryCountryPath(country)}/${category.slug}`

/* ------------------------------------------------------------------ *
 *  Titles
 * ------------------------------------------------------------------ */

/**
 * The page-name half of a category page's title. `<Seo>` appends " - Bazio".
 * The year is the SERVER's current year, passed in, so a page never carries a
 * hardcoded year and server and browser always render the same string.
 */
export const directoryCategoryTitle = (country: DirectoryCountry, category: DirectoryCategory, year: number): string =>
    `Best Telegram ${category.name} Groups in ${country.label} (${year})`

export const directoryCategoryHeading = (country: DirectoryCountry, category: DirectoryCategory): string =>
    `Best Telegram ${category.name} Groups in ${country.label}`

export const directoryHubTitle = (country: DirectoryCountry): string => `Telegram Groups in ${country.label}`

/* ------------------------------------------------------------------ *
 *  Freshness
 * ------------------------------------------------------------------ */

/**
 * A group checked within this many days is FRESH: it is listed and it counts
 * towards a page's indexing threshold.
 */
export const DIRECTORY_FRESH_DAYS = 90

/**
 * Between FRESH and this many days a group is DUE for a re-check: still
 * listed, with its real check date shown, but no longer counted towards
 * indexing. Past it, the group leaves the public page on its own until an
 * editor checks it again. Nothing is deleted.
 */
export const DIRECTORY_LISTED_MAX_DAYS = 180

/** A member count older than this is not shown, however it was read. */
export const DIRECTORY_MEMBER_COUNT_MAX_DAYS = 180

export type EntryFreshness = 'fresh' | 'due' | 'expired' | 'never'

const DAY_MS = 24 * 60 * 60 * 1000

export const entryFreshness = (lastVerifiedAt: string | Date | null, now: Date = new Date()): EntryFreshness => {
    if (!lastVerifiedAt) return 'never'

    const at = lastVerifiedAt instanceof Date ? lastVerifiedAt : new Date(lastVerifiedAt)

    if (Number.isNaN(at.getTime())) return 'never'

    const age = now.getTime() - at.getTime()

    if (age <= DIRECTORY_FRESH_DAYS * DAY_MS) return 'fresh'
    if (age <= DIRECTORY_LISTED_MAX_DAYS * DAY_MS) return 'due'

    return 'expired'
}

export type DirectoryEntryState = {
    status: string
    publicJoinAvailable: boolean
    lastVerifiedAt: string | Date | null
}

/**
 * Whether a group may appear on a public page at all. Only an `active`,
 * joinable group checked within `DIRECTORY_LISTED_MAX_DAYS`; a `stale`,
 * `hidden` or `archived` one never, and neither does one nobody has checked.
 */
export const isPubliclyListed = (entry: DirectoryEntryState, now: Date = new Date()): boolean => {
    if (entry.status !== 'active' || !entry.publicJoinAvailable) return false

    const freshness = entryFreshness(entry.lastVerifiedAt, now)

    return freshness === 'fresh' || freshness === 'due'
}

/** Whether a listed group counts towards its page's indexing threshold. */
export const countsTowardIndex = (entry: DirectoryEntryState, now: Date = new Date()): boolean =>
    entry.status === 'active' && entry.publicJoinAvailable && entryFreshness(entry.lastVerifiedAt, now) === 'fresh'

/** The SQL half of `isPubliclyListed`, on the `e` alias. */
export const DIRECTORY_LISTED_SQL = `(e.status = 'active' AND e.public_join_available AND e.last_verified_at >= strftime('%Y-%m-%dT%H:%M:%fZ', 'now', '-${DIRECTORY_LISTED_MAX_DAYS} days'))`

/** The SQL half of `countsTowardIndex`, on the `e` alias. */
export const DIRECTORY_FRESH_SQL = `(e.status = 'active' AND e.public_join_available AND e.last_verified_at >= strftime('%Y-%m-%dT%H:%M:%fZ', 'now', '-${DIRECTORY_FRESH_DAYS} days'))`

/* ------------------------------------------------------------------ *
 *  Indexing
 * ------------------------------------------------------------------ */

/** The default number of FRESH groups a page needs before it may be indexed. */
export const DIRECTORY_MIN_ENTRIES_DEFAULT = 5

/** The lowest an editor may set that number to, deliberately, per page. */
export const DIRECTORY_MIN_ENTRIES_FLOOR = 3

export type DirectoryPageSettings = {
    seoEnabled: boolean
    minEntries: number
}

/** A page nobody has configured: not indexed. */
export const DEFAULT_PAGE_SETTINGS: DirectoryPageSettings = {
    seoEnabled: false,
    minEntries: DIRECTORY_MIN_ENTRIES_DEFAULT
}

export type DirectoryIndexDecision =
    | { indexable: true, reason: 'ready' }
    | { indexable: false, reason: 'not_enabled' | 'too_few' }

/**
 * Whether ONE country/category page may be indexed.
 *
 * Both conditions, always: an editor switched it on AND it holds at least its
 * minimum of freshly checked groups. Switching it on never indexes a page with
 * one group, and a page that loses groups to staleness drops out on its own.
 */
export const directoryIndexDecision = (settings: DirectoryPageSettings, freshCount: number): DirectoryIndexDecision => {
    if (!settings.seoEnabled) return { indexable: false, reason: 'not_enabled' }

    const minimum = Math.max(DIRECTORY_MIN_ENTRIES_FLOOR, settings.minEntries)

    if (freshCount < minimum) return { indexable: false, reason: 'too_few' }

    return { indexable: true, reason: 'ready' }
}

/* ------------------------------------------------------------------ *
 *  Display
 * ------------------------------------------------------------------ */

/** "18K", "1.2K", "850". Never rounds a small group up. */
export const formatMemberCount = (count: number): string => {
    if (count >= 1_000_000) return `${(count / 1_000_000).toFixed(1).replace(/\.0$/, '')}M`
    if (count >= 10_000) return `${Math.floor(count / 1000)}K`
    if (count >= 1000) return `${(Math.floor(count / 100) / 10).toFixed(1).replace(/\.0$/, '')}K`

    return String(count)
}

/**
 * The member line for a card, or null when the count is missing or too old to
 * state. A count is never estimated and never shown without being dated.
 */
export const memberCountLine = (
    entry: { memberCount: number | null, memberCountCheckedAt: string | null, entityType: string },
    now: Date = new Date()
): string | null => {
    if (entry.memberCount === null || !entry.memberCountCheckedAt) return null

    const checked = new Date(entry.memberCountCheckedAt)

    if (Number.isNaN(checked.getTime())) return null
    if (now.getTime() - checked.getTime() > DIRECTORY_MEMBER_COUNT_MAX_DAYS * DAY_MS) return null

    return `${formatMemberCount(entry.memberCount)} ${entry.entityType === 'channel' ? 'subscribers' : 'members'}`
}

/* ------------------------------------------------------------------ *
 *  The bridge to /buy/<country>
 * ------------------------------------------------------------------ */

export type BuyPageStatus =
    | { state: 'live', path: string }
    | { state: 'eligible' }
    | { state: 'not_eligible' }

/**
 * What the admin summary says about a directory country's own Bazio inventory
 * page. Read only: nothing here creates or indexes a `/buy/` page.
 *
 *   live          the country is configured in `SEO_COUNTRIES`
 *   eligible      not configured, but it already holds enough distinct Store
 *                 items that a person may now choose to add it there
 *   not_eligible  everything else, which is every directory country today
 */
export const buyPageStatus = (countryCode: string, distinctStoreItems: number): BuyPageStatus => {
    const configured = seoCountryByCode(countryCode)

    if (configured) return { state: 'live', path: `/buy/${configured.slug}` }
    if (distinctStoreItems >= SEO_INDEX_MIN_ITEMS) return { state: 'eligible' }

    return { state: 'not_eligible' }
}

/* ------------------------------------------------------------------ *
 *  Shared copy
 * ------------------------------------------------------------------ */

/** The methodology behind the word "best". The same everywhere, because it is one process. */
export const DIRECTORY_METHODOLOGY = 'The Bazio team reviews public Telegram communities for how relevant they are to buying and selling, whether anyone can join, signs of recent activity, how useful they are to people in that country, and whether the public link still works. Group size may be considered, but it is never the only factor. Every group is re-checked regularly, and a group that has not been checked within six months leaves this page until it is checked again.'

/** Bazio lists these groups; it does not run, endorse or moderate them. */
export const DIRECTORY_TRUST_NOTICE = 'Bazio lists public communities for discovery. Group content, transactions and moderation are controlled by their respective administrators. Verify sellers and offers independently before transacting.'
